import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../state/app.state';
import * as WorkspaceActions from '../../../../../state/workspace/workspace.actions';
import * as WorkspaceSelectors from '../../../../../state/workspace/workspace.selectors';
import { Workspace } from '../../../../../models/workspace.model';

@Component({
  selector: 'app-workspace-selection-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './workspace-selection-modal.component.html',
  styleUrl: './workspace-selection-modal.component.scss'
})
export class WorkspaceSelectionModalComponent implements OnInit, OnDestroy {
  @Output() reportGenerated = new EventEmitter<Workspace>();
  @Output() modalClosed = new EventEmitter<void>();

  private destroy$ = new Subject<void>();

  workspaces$: Observable<Workspace[]>;
  loading$: Observable<boolean>;
  error$: Observable<string | null>;
  selectedWorkspace: Workspace | null = null;
  validationError: string | null = null;

  constructor(private store: Store<AppState>) {
    this.workspaces$ = this.store.select(WorkspaceSelectors.selectAllWorkspaces);
    this.loading$ = this.store.select(WorkspaceSelectors.selectWorkspacesLoading);
    this.error$ = this.store.select(WorkspaceSelectors.selectWorkspacesError);
  }

  ngOnInit(): void {
    // Load workspaces if not already loaded
    this.store.dispatch(WorkspaceActions.loadWorkspaces());

    // Auto-select default workspace when workspaces are loaded
    this.workspaces$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(workspaces => {
      if (workspaces && workspaces.length > 0) {
        const defaultWorkspace = workspaces.find(w => w.is_default);
        if (defaultWorkspace) {
          // Set default workspace as selected but don't generate report yet
          this.selectedWorkspace = defaultWorkspace;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  selectWorkspace(workspace: Workspace): void {
    this.selectedWorkspace = workspace;
    this.validationError = null;
  }

  generateReport(): void {
    if (!this.selectedWorkspace) {
      this.validationError = 'Please select a workspace to continue.';
      return;
    }
    const workspace_id = this.selectedWorkspace.id;
    if (typeof workspace_id !== 'string' || workspace_id.trim() === '') {
      this.validationError = "We couldn't use that workspace. Please pick a different one or refresh the page.";
      return;
    }
    this.validationError = null;
    this.reportGenerated.emit(this.selectedWorkspace);
  }

  closeModal(): void {
    this.modalClosed.emit();
  }

  onOverlayClick(event: Event): void {
    // Close modal when clicking on overlay (background)
    this.closeModal();
  }
}